'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  buildAuthorizeUrl,
  buildLogoutUrl,
  buildRefreshTokenBody,
  buildTokenExchangeBody,
  COGNITO_PKCE_STORAGE_KEY,
  COGNITO_TOKEN_STORAGE_KEY,
  createStoredTokenSession,
  DEFAULT_ADMIN_GROUPS,
  getCognitoGroups,
  isAdminAccessToken,
  shouldClearPkceOnInitialSessionLoad,
  type CognitoTokenSession,
  type StoredPkceState,
} from "@/lib/cognitoAdminAuth";

type AuthContextValue = {
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isPlatformAdmin: boolean;
  aiRole: "viewer" | "editor" | "administrator" | null;
  login: () => Promise<void>;
  handleRedirectCallback: (code: string, state: string) => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const HOSTED_UI_BASE_URL = process.env.COGNITO_HOSTED_UI_BASE_URL?.replace(/\/+$/, "");
const ADMIN_CLIENT_ID = process.env.COGNITO_ADMIN_CLIENT_ID;
const ADMIN_GROUPS = (process.env.COGNITO_ADMIN_GROUPS || DEFAULT_ADMIN_GROUPS.join(","))
  .split(",")
  .map((group) => group.trim())
  .filter(Boolean);
const AI_GROUPS = ["ai-viewers", "ai-editors", "ai-administrators"];
const ACCESS_GROUPS = [...new Set([...ADMIN_GROUPS, ...AI_GROUPS])];

const getLoginRedirectUri = () => `${window.location.origin}/auth/callback`;
const getLogoutRedirectUri = () => `${window.location.origin}/login`;

const toBase64Url = (bytes: Uint8Array): string => {
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const createRandomString = (): string => {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
};

const createPkceChallenge = async (verifier: string): Promise<string> => {
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return toBase64Url(new Uint8Array(digest));
};

const parseStoredSession = (): CognitoTokenSession | null => {
  const raw = window.localStorage.getItem(COGNITO_TOKEN_STORAGE_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as CognitoTokenSession;
    if (!session.accessToken || !isAdminAccessToken(session.accessToken, ACCESS_GROUPS)) return null;
    return session;
  } catch {
    return null;
  }
};

const assertAuthConfig = () => {
  if (!HOSTED_UI_BASE_URL || !ADMIN_CLIENT_ID) {
    throw new Error("Missing Cognito admin Hosted UI configuration.");
  }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<CognitoTokenSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearSession = useCallback(() => {
    window.localStorage.removeItem(COGNITO_TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(COGNITO_PKCE_STORAGE_KEY);
    window.localStorage.removeItem("adminToken");
    setSession(null);
  }, []);

  const storeSession = useCallback((nextSession: CognitoTokenSession) => {
    if (!isAdminAccessToken(nextSession.accessToken, ACCESS_GROUPS)) {
      throw new Error("Admin or AI operations privileges required.");
    }
    window.localStorage.setItem(COGNITO_TOKEN_STORAGE_KEY, JSON.stringify(nextSession));
    window.localStorage.removeItem("adminToken");
    setSession(nextSession);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      setIsLoading(false);
      return;
    }
    const existingSession = parseStoredSession();
    window.localStorage.removeItem("adminToken");
    if (existingSession) {
      setSession(existingSession);
    } else {
      window.localStorage.removeItem(COGNITO_TOKEN_STORAGE_KEY);
      if (shouldClearPkceOnInitialSessionLoad(window.location.pathname)) {
        window.localStorage.removeItem(COGNITO_PKCE_STORAGE_KEY);
      }
      setSession(null);
    }
    setIsLoading(false);
  }, [clearSession]);

  const login = useCallback(async () => {
    assertAuthConfig();
    setIsLoading(true);
    try {
      const codeVerifier = createRandomString();
      const state = createRandomString();
      const redirectUri = getLoginRedirectUri();
      const codeChallenge = await createPkceChallenge(codeVerifier);
      const pkceState: StoredPkceState = { state, codeVerifier, redirectUri };
      window.localStorage.setItem(COGNITO_PKCE_STORAGE_KEY, JSON.stringify(pkceState));
      window.location.assign(
        buildAuthorizeUrl({
          hostedUiBaseUrl: HOSTED_UI_BASE_URL!,
          clientId: ADMIN_CLIENT_ID!,
          redirectUri,
          state,
          codeChallenge,
        }),
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleRedirectCallback = useCallback(async (code: string, state: string) => {
    assertAuthConfig();
    const storedPkce = window.localStorage.getItem(COGNITO_PKCE_STORAGE_KEY);
    if (!storedPkce) {
      throw new Error("Missing Cognito login state. Start sign in again.");
    }
    const pkceState = JSON.parse(storedPkce) as StoredPkceState;
    if (pkceState.state !== state) {
      throw new Error("Cognito login state mismatch. Start sign in again.");
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${HOSTED_UI_BASE_URL}/oauth2/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: buildTokenExchangeBody({
          clientId: ADMIN_CLIENT_ID!,
          code,
          codeVerifier: pkceState.codeVerifier,
          redirectUri: pkceState.redirectUri,
        }),
      });
      if (!response.ok) {
        throw new Error("Cognito token exchange failed.");
      }
      const tokens = await response.json();
      if (!tokens.access_token) {
        throw new Error("Cognito token exchange did not return an access token.");
      }
      storeSession(
        createStoredTokenSession({
          accessToken: tokens.access_token,
          idToken: tokens.id_token,
          refreshToken: tokens.refresh_token,
          expiresIn: tokens.expires_in,
        }),
      );
      window.localStorage.removeItem(COGNITO_PKCE_STORAGE_KEY);
    } catch (error) {
      clearSession();
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [clearSession, storeSession]);

  const getAccessToken = useCallback(async () => {
    if (!session) return null;
    if (session.expiresAt > Date.now() + 60_000) {
      return session.accessToken;
    }
    if (!session.refreshToken || !HOSTED_UI_BASE_URL || !ADMIN_CLIENT_ID) {
      clearSession();
      return null;
    }

    try {
      const response = await fetch(`${HOSTED_UI_BASE_URL}/oauth2/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: buildRefreshTokenBody({
          clientId: ADMIN_CLIENT_ID,
          refreshToken: session.refreshToken,
        }),
      });
      if (!response.ok) {
        clearSession();
        return null;
      }
      const tokens = await response.json();
      if (!tokens.access_token) {
        clearSession();
        return null;
      }
      const refreshedSession = createStoredTokenSession({
        accessToken: tokens.access_token,
        idToken: tokens.id_token || session.idToken,
        refreshToken: tokens.refresh_token || session.refreshToken,
        expiresIn: tokens.expires_in,
      });
      storeSession(refreshedSession);
      return refreshedSession.accessToken;
    } catch {
      clearSession();
      return null;
    }
  }, [clearSession, session, storeSession]);

  const logout = useCallback(() => {
    clearSession();
    if (HOSTED_UI_BASE_URL && ADMIN_CLIENT_ID) {
      window.location.assign(
        buildLogoutUrl({
          hostedUiBaseUrl: HOSTED_UI_BASE_URL,
          clientId: ADMIN_CLIENT_ID,
          logoutUri: getLogoutRedirectUri(),
        }),
      );
    }
  }, [clearSession]);

  const value = useMemo(
    () => {
      const groups = new Set(session?.accessToken ? getCognitoGroups(session.accessToken) : []);
      const isPlatformAdmin = ADMIN_GROUPS.some((group) => groups.has(group));
      const aiRole =
        groups.has("super-admins") || groups.has("ai-administrators")
          ? "administrator"
          : groups.has("admins") || groups.has("ai-editors")
            ? "editor"
            : groups.has("ai-viewers")
              ? "viewer"
              : null;
      return {
      token: session?.accessToken ?? null,
      isAuthenticated: Boolean(session?.accessToken),
      isLoading,
      isPlatformAdmin,
      aiRole,
      login,
      handleRedirectCallback,
      getAccessToken,
      logout,
      } as AuthContextValue;
    },
    [session?.accessToken, isLoading, login, handleRedirectCallback, getAccessToken, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider.");
  }
  return ctx;
};
